import React, { useState, useRef } from 'react';
import { FileUp, Download, FileText, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useCapabilities } from '../hooks/useCapabilities';

const LocalInvoiceManager = ({
    authFetch,
    updateSectionData,
    imaginaData
}) => {
    const { hasCapability } = useCapabilities();
    const hasVentasElectricidad = hasCapability('ventas_electricidad');
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [status, setStatus] = useState('IDLE'); // IDLE, ANALYZING, ERROR
    const [extractedData, setExtractedData] = useState(null);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setSelectedFile(file);
            setFileUrl(URL.createObjectURL(file));
            setStatus('IDLE');
            setExtractedData(null);
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        if (fileUrl) URL.revokeObjectURL(fileUrl);
        setFileUrl(null);
        setStatus('IDLE');
        setExtractedData(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownload = () => {
        if (fileUrl && selectedFile) {
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = selectedFile.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    const handleAnalyze = async () => {
        if (!selectedFile) return;
        setStatus('ANALYZING');
        
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            const res = await authFetch('/api/sale/extract', {
                method: 'POST',
                body: formData
            });

            if (!res.ok) {
                let errorMsg = 'Error en la extracción';
                try {
                    const errorData = await res.json();
                    errorMsg = errorData.error || errorData.message || errorData.details || JSON.stringify(errorData);
                } catch (e) {
                    errorMsg = `Error ${res.status}: ${res.statusText}`;
                }
                throw new Error(errorMsg);
            }
            
            const data = await res.json();
            const extracted = (data.success && data.data) ? data.data : (data.data || data);
            
            if (extracted) {
                setExtractedData(extracted);
                
                const getSection = (name) => {
                    if (extracted[name]) return extracted[name];
                    if (name === 'cliente' && (extracted.nombre_cliente || extracted.dni_cif)) return extracted;
                    if (name === 'contrato' && extracted.cups) return extracted;
                    if (name === 'consumo' && (extracted.consumo_p1 || extracted.consumo_p1_kWh)) return extracted;
                    if (name === 'potencia' && extracted.potencia_p1_kw) return extracted;
                    if (name === 'precios' && (extracted.precio_energia_p1 || extracted['precio_energia_p1_€'])) return extracted;
                    return {};
                };

                const cliente = getSection('cliente');
                const contrato = getSection('contrato');
                const potencia = getSection('potencia');
                const consumo = getSection('consumo');

                const updates = {};
                
                const getNested = (obj, keys) => {
                    for (const k of keys) {
                        if (obj && obj[k]) return obj[k];
                    }
                    return null;
                };

                const name = getNested(cliente, ['nombre_cliente', 'full_name', 'nombre_completo', 'titular', 'nombre']) || 
                             getNested(extracted, ['nombre_cliente', 'full_name', 'nombre_completo', 'titular', 'nombre']);
                if (name) updates.full_name = name;
                
                const id = getNested(cliente, ['dni_cif', 'nif', 'dni', 'cif', 'documento_identidad']) || 
                           getNested(extracted, ['dni_cif', 'nif', 'dni', 'cif', 'documento_identidad']);
                if (id) updates.nif = id;
                
                const addr = getNested(cliente, ['direccion_suministro', 'address_full', 'direccion', 'domicilio']) || 
                             getNested(extracted, ['direccion_suministro', 'address_full', 'direccion', 'domicilio']);
                if (addr) updates.address_full = addr;
                
                const c = getNested(contrato, ['cups']) || getNested(extracted, ['cups']) || getNested(cliente, ['cups']);
                if (c) updates.cups = c;

                ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].forEach(p => {
                    const val = getNested(potencia, [`potencia_${p}_kw`, `potencia_${p}`, p, `${p}_kw`]) || 
                                getNested(extracted, [`potencia_${p}_kw`, `potencia_${p}`]);
                    if (val) updates[`potencia_${p}`] = String(val);

                    // Map current prices to SalesForm new fields
                    const pr = getSection('precios') || getSection('precio') || extracted;
                    
                    // Extraer precio energía: buscar en 'energia.p1', 'precio_energia_p1', etc.
                    let eVal = getNested(pr, [`precio_energia_${p}`, `energia_${p}`, `precio_energia_${p}_€`, `precio_energia_${p}_€/kWh`]);
                    if (!eVal && pr.energia) eVal = getNested(pr.energia, [p, `valor`, `precio`]);
                    if (!eVal && extracted[`precio_energia_${p}`]) eVal = extracted[`precio_energia_${p}`];
                    if (!eVal && extracted[`precio_energia_${p}_€/kWh`]) eVal = extracted[`precio_energia_${p}_€/kWh`];
                    if (eVal) updates[`current_price_energy_${p}`] = String(eVal);
                    
                    // Extraer precio potencia
                    let pVal = getNested(pr, [`precio_potencia_${p}`, `potencia_${p}`, `precio_potencia_${p}_€`, `precio_potencia_${p}_€/kW`]);
                    if (!pVal && pr.potencia) pVal = getNested(pr.potencia, [p, `valor`, `precio`]);
                    if (!pVal && extracted[`precio_potencia_${p}`]) pVal = extracted[`precio_potencia_${p}`];
                    if (!pVal && extracted[`precio_potencia_${p}_€/kW`]) pVal = extracted[`precio_potencia_${p}_€/kW`];
                    if (pVal) updates[`current_price_power_${p}`] = String(pVal);
                });

                const caeVal = getNested(consumo, ['consumo_anual_estimado_kwh', 'cae', 'estimacion_anual']) || 
                               getNested(extracted, ['consumo_anual_estimado_kwh', 'cae']);
                if (caeVal) updates.cae = String(caeVal);

                if (Object.keys(updates).length > 0) {
                    updateSectionData('SalesForm', updates, 'invoice');
                }
                setStatus('IDLE');
            } else {
                throw new Error('No se pudieron extraer datos de la factura');
            }
        } catch (err) {
            console.error('Error in handleAnalyze:', err);
            setError(err.message || 'Error al extraer datos de la factura.');
            setStatus('ERROR');
        }
    };

    return (
        <div className="flex flex-col items-center justify-center p-4 bg-slate-900/50 border border-slate-700/50 rounded-2xl transition-all group shadow-inner relative">
            {status === 'ANALYZING' ? (
                <>
                    <Loader2 size={24} className="mb-2 animate-spin text-primary" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-primary">Analizando...</span>
                </>
            ) : !selectedFile ? (
                <>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!hasVentasElectricidad}
                        className={`flex flex-col items-center transition-all ${
                            hasVentasElectricidad 
                            ? 'hover:scale-105' 
                            : 'opacity-50 grayscale cursor-not-allowed'
                        }`}
                        title={hasVentasElectricidad ? "Subir Factura" : "No tienes permiso para gestionar facturas de electricidad"}
                    >
                        <FileUp size={24} className={`mb-2 ${hasVentasElectricidad ? 'text-slate-400 group-hover:text-blue-400' : 'text-slate-500'} transition-colors`} />
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${hasVentasElectricidad ? 'text-slate-500 group-hover:text-blue-400' : 'text-slate-600'}`}>
                            {hasVentasElectricidad ? 'Subir Factura' : 'Acceso Denegado'}
                        </span>
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="application/pdf"
                        onChange={handleFileChange}
                    />
                </>
            ) : (
                <div className="flex flex-col items-center w-full relative">
                    <button
                        type="button"
                        onClick={handleClearFile}
                        className="absolute top-2 right-2 p-1.5 bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded-lg border border-slate-700 hover:border-red-500/30 transition-all z-10 opacity-0 group-hover:opacity-100"
                        title="Quitar"
                    >
                        <X size={14} />
                    </button>
                    
                    <FileText size={20} className="mb-1 text-blue-400" />
                    <span className="text-[9px] font-bold text-slate-300 w-full text-center truncate px-2 mb-2">
                        {selectedFile.name}
                    </span>

                    <div className="flex gap-1.5 w-full mt-1">
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="flex-1 flex items-center justify-center py-1.5 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white rounded-lg transition-colors"
                        >
                            <Download size={14} />
                        </button>
                        
                        <button
                            type="button"
                            onClick={handleAnalyze}
                            className={`flex-1 flex items-center justify-center py-1.5 rounded-lg border transition-all ${
                                !hasVentasElectricidad
                                ? 'bg-slate-700/30 border-slate-600/30 text-slate-500 opacity-50 grayscale cursor-not-allowed'
                                : extractedData 
                                    ? 'bg-emerald-900/50 border-emerald-500/20 text-emerald-500 opacity-60 cursor-not-allowed'
                                    : 'bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 border-emerald-500/20 cursor-pointer'
                            }`}
                            disabled={!!extractedData || !hasVentasElectricidad}
                            title={!hasVentasElectricidad ? "No tienes permiso" : extractedData ? "Ya analizada" : "Analizar"}
                        >
                            {extractedData ? <CheckCircle2 size={14} /> : <FileText size={14} />}
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-700/50 rounded-3xl p-8 max-w-sm w-full shadow-2xl flex flex-col items-center text-center relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-red-500/10 blur-2xl rounded-full" />
                        <div className="relative p-4 bg-red-500/10 rounded-full mb-6 mt-2">
                            <AlertCircle size={40} className="text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-100 mb-3">Error en la Factura</h3>
                        <p className="text-sm text-slate-400 mb-8 whitespace-pre-wrap">{error}</p>
                        <button
                            type="button"
                            onClick={() => setError(null)}
                            className="w-full py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl font-bold uppercase tracking-widest text-xs border border-slate-700"
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocalInvoiceManager;
